import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { WEEKDAYS, formatDay, formatDuration, heatLevel } from './activityFormat';

// Grafiklar faqat ko'rinish: ma'lumot tayyor holda keladi. Ranglar tokens.css dan.
// Animatsiya o'chiq: davr o'zgarganda grafik noldan qayta "o'smaydi", eski kadr joyida yangilanadi.
const AXIS_TICK = { fill: 'var(--text-muted)', fontSize: 12 };
const AXIS_LINE = { stroke: 'var(--border-strong)' };
const HOUR_LABELS = new Set([0, 6, 12, 18, 23]);

function ChartTooltip({ active, payload, render }) {
  if (!active || !payload?.length) return null;
  return <div className="ui-la-tooltip">{render(payload[0].payload)}</div>;
}

// Kunlik ustunlar. data: [{ date, ...}], valueKey — ustun balandligi,
// tooltip(row) — ko'rsatiladigan qatorlar, tickFormatter — Y o'qi.
export function DailyBars({ data, valueKey, tooltip, tickFormatter, label }) {
  return (
    <div className="ui-la-chart" role="img" aria-label={label}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => formatDay(value)}
            tick={AXIS_TICK}
            axisLine={AXIS_LINE}
            tickLine={false}
            minTickGap={16}
          />
          <YAxis
            tick={AXIS_TICK}
            axisLine={false}
            tickLine={false}
            width={44}
            allowDecimals={false}
            tickFormatter={tickFormatter}
          />
          <Tooltip cursor={{ fill: 'var(--bg-subtle)' }} content={<ChartTooltip render={tooltip} />} />
          <Bar dataKey={valueKey} fill="var(--chart-2)" radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Hafta kuni x soat (Toshkent vaqti). cells: [{ weekday: 0..6, hour: 0..23, seconds }]
export function HourHeatmap({ cells }) {
  const grid = WEEKDAYS.map(() => Array(24).fill(0));
  cells.forEach(({ weekday, hour, seconds }) => {
    if (grid[weekday] && hour >= 0 && hour < 24) grid[weekday][hour] = seconds;
  });
  const max = Math.max(0, ...cells.map((cell) => cell.seconds));

  return (
    <div className="ui-la-heat">
      <div className="ui-la-heat__grid" role="table" aria-label="Hafta kuni va soat bo'yicha o'qish vaqti">
        <div className="ui-la-heat__row" role="row">
          <span className="ui-la-heat__label" role="columnheader" />
          {grid[0].map((_, hour) => (
            <span key={hour} className="ui-la-heat__hour" role="columnheader">
              {HOUR_LABELS.has(hour) ? hour : ''}
            </span>
          ))}
        </div>
        {grid.map((hours, weekday) => (
          <div key={WEEKDAYS[weekday]} className="ui-la-heat__row" role="row">
            <span className="ui-la-heat__label" role="rowheader">{WEEKDAYS[weekday]}</span>
            {hours.map((seconds, hour) => {
              const text = `${WEEKDAYS[weekday]}, ${hour}:00–${hour + 1}:00 · ${formatDuration(seconds)}`;
              return (
                <span
                  key={hour}
                  role="cell"
                  title={text}
                  aria-label={text}
                  className={`ui-la-heat__cell ui-la-heat__cell--${heatLevel(seconds, max)}`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="ui-la-heat__legend" aria-hidden="true">
        <span>Kam</span>
        {[1, 2, 3, 4, 5].map((level) => (
          <span key={level} className={`ui-la-heat__cell ui-la-heat__cell--${level}`} />
        ))}
        <span>Ko'p</span>
        <span className="ui-la-heat__legend-gap" />
        <span className="ui-la-heat__cell ui-la-heat__cell--0" />
        <span>Ko'rilmagan</span>
      </div>
    </div>
  );
}

// Ko'rilgan vaqt (soat) va o'rtacha imtihon balli. data: [{ name, hours, score }]
export function TimeScoreScatter({ data }) {
  return (
    <div className="ui-la-chart" role="img" aria-label="Ko'rilgan vaqt va o'rtacha imtihon balli">
      <ResponsiveContainer width="100%" height={260}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="var(--border)" />
          <XAxis
            type="number"
            dataKey="hours"
            name="Soat"
            tick={AXIS_TICK}
            axisLine={AXIS_LINE}
            tickLine={false}
          />
          <YAxis type="number" dataKey="score" name="Ball" tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} />
          <ZAxis range={[72, 72]} />
          <Tooltip
            cursor={false}
            content={
              <ChartTooltip
                render={(row) => (
                  <>
                    <b>{row.score}</b>
                    <span>o'rtacha ball</span>
                    <b>{formatDuration(row.seconds)}</b>
                    <span>{row.name}</span>
                  </>
                )}
              />
            }
          />
          <Scatter data={data} fill="var(--chart-2)" stroke="var(--bg-surface)" strokeWidth={2} isAnimationActive={false} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
